import {distinct, groupByArr, sum, useAsyncAction} from "nate-react-api-helpers";
import {Alert, Button, DialogContent, DialogTitle, Grid} from "@mui/material";
import {Dialog2} from "../../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../../misc/SmoothActionBar";
import React, {useMemo, useRef, useState} from "react";
import {CanvasRef, PrepCanvas} from "./PrepCanvas";
import {PrepControls} from "./PrepControls";
import {PrepCanvasManager, PrepValues} from "./PrepCanvasManager";
import {ImperialDistance} from "../../../../../misc/ImperialDistance";
import {useSyncedRef} from "../../../../../misc/SyncedRef";
import {HardwareGroupWithContent, HardwareItem} from "../../../../../api/Hardware";
import {PrepDetail, PrepFromGroup} from "../../../../../api/QuoteHardware";
import {dbPrep, dbStrikePrep} from "../../extras/PrepMenuOptions";
import {genericDistanceParser} from "../../../../../misc/scroller/Table";
import {PrepConfigCustomOffset, Product} from "../../../../../api/Products";
import {AutoCalculate} from "./AutoCalculate";
import {DimensionInput2} from "../../../../manufacturing/DimensionInput2";
import {getDefaultHingeQty} from "../MultiHingeConfig";
import {UnitSystem} from "../../../../../api/Projects";
import {useProjectUnitSystem} from "../../ProjectName";

export function usePrepManagerDetail(props: {
    value: HardwareGroupWithContent;
    clearExisting?: boolean;
}) {
    const sys = useProjectUnitSystem();
    const {heights, inactiveWidths, widths, pair, overrideHingeQty, hingeHeight} = useMemo(() => {
        const heights = distinct(props.value.openings.map(o => o.nominalHeight));
        const widths = distinct(props.value.openings.map(o => o.nominalWidth));
        const inactiveWidths = distinct(props.value.openings.map(o => o.inactiveDoorWidth));
        const pair = distinct(props.value.openings.map(o => o.openingType === "Pair"));

        const hingeHeight = getHingeHeight(props.value.hardware);

        let overrideHingeQty = !sys ? undefined : getHingeOverrideQty(props.value.hardware, heights.length > 0 ? heights[0] : "", sys);

        return {heights, widths, inactiveWidths, pair, overrideHingeQty, hingeHeight}
    }, [props.value, sys]);

    return {
        heights, widths, inactiveWidths,
        hingeHeight,
        overrideHingeQty,
        hasDeadbolt: hasDeadbolt(props.value.hardware, pair[0]),
        isPair: pair[0],
    }
}

export function usePrepManager2(props: {
    height: string;
    width: string;
    inactiveWidth?: string;
    isPair: boolean;
    config: PrepDetail[] | undefined;
    hingeHeight: number;
    overrideHingeQty?: number;
    hasDeadbolt: boolean;
    overCutDoor: string | undefined;
    underCutDoor: string | undefined;
    underCutFrame: string | undefined;
}) {
    const manager = useMemo(() => {
        let height = props.height;
        if(!height) height = "8'";

        let widthStr = props.width;
        if(!widthStr) widthStr = "4'";

        let inactiveDoorWidth = props.inactiveWidth || "3'";

        const config = convertPrepConfig(props.config);
        const isPair = props.isPair;

        return new PrepCanvasManager({
            nominalHeight: genericDistanceParser(height).universalValue(),
            nominalWidth: genericDistanceParser(widthStr).universalValue(),
            hingeHeight: props.hingeHeight,
            overrides: config,
            secondaryWidth: isPair ? genericDistanceParser(inactiveDoorWidth).universalValue() : undefined,
            pair: isPair,
            overrideHingeQty: props.overrideHingeQty || 0,
            hasDeadbolt: props.hasDeadbolt,
            overCutDoor: props.overCutDoor ? safeDimParse(props.overCutDoor) || 0 : 0,
            underCutDoor: props.underCutDoor ? safeDimParse(props.underCutDoor) || 0 : 0,
            underCutFrame: props.underCutFrame ? safeDimParse(props.underCutFrame) || 0 : 0,
        });
    }, [
        props.height, props.width, props.inactiveWidth, props.isPair, props.config,
        props.hingeHeight, props.overrideHingeQty, props.hasDeadbolt,
        props.overCutDoor, props.underCutDoor, props.underCutFrame
    ]);

    return manager;
}


export function getHingeHeight(input: Pick<HardwareItem, "dimHeight" | "productIsAutoQtyHinge" | "overrideHingeActiveQty" | "overrideHingeInActiveQty">[]) {
    const hinge = input.find(h => h.productIsAutoQtyHinge || h.overrideHingeActiveQty || h.overrideHingeInActiveQty);
    return ImperialDistance.parse(hinge?.dimHeight || "4.5\"").universalValue();
}

export function getHingeOverrideQty(
    input: Pick<HardwareItem, "overrideHingeActiveQty" | "overrideHingeInActiveQty" | "productIsAutoQtyHinge" | "hingeActiveQtys" | "hingeInactiveQtys">[],
    openingHeight: string,
    unitSystem?: UnitSystem
) {

    const defaultHingeQty = openingHeight ? getDefaultHingeQty(openingHeight, unitSystem) : 999999;

    if(input.find(h => h.overrideHingeActiveQty !== null && h.overrideHingeActiveQty !== undefined)) {
        const hingeActiveOverride = input
            .filter(h => h.overrideHingeActiveQty || h.productIsAutoQtyHinge)
            .map(h => {
                if(h.overrideHingeActiveQty === null || h.overrideHingeActiveQty === undefined) {
                    return sum(h.hingeActiveQtys || [])
                }

                return h.overrideHingeActiveQty
            });

        const qty = sum(hingeActiveOverride);
        if(qty === defaultHingeQty) return undefined;
        return qty;
    }

    if(input.find(h => h.overrideHingeInActiveQty !== null && h.overrideHingeInActiveQty !== undefined)) {
        const hingeInactiveOverride = input
            .filter(h => h.overrideHingeInActiveQty || h.productIsAutoQtyHinge)
            .map(h => {
                if (h.overrideHingeInActiveQty === null || h.overrideHingeInActiveQty === undefined) {
                    return sum(h.hingeInactiveQtys || [])
                }

                return h.overrideHingeInActiveQty
            })

        const qty = sum(hingeInactiveOverride);
        if(qty === defaultHingeQty) return undefined;
        return qty;
    }

    return undefined;
}

type TPreps = "hwPrepSingleActiveDoor" | "hwPrepSingleFrame" | "hwPrepPairActiveDoor" | "hwPrepPairInactiveDoor" | "hwPrepPairFrame";

// whether a single hardware item carries a deadbolt prep (deadbolt bore or its strike) — the same
// test the legacy Customize Hardware Preps uses to decide the deadbolt is present. The elevation tool
// reuses this to flag which item is the deadbolt (see the Hardware manage dialog + the dbd projection).
export function isDeadboltItem(h: Product | Pick<HardwareItem, TPreps>, isPair: boolean) {
    if(isPair) {
        if(h.hwPrepPairActiveDoor === dbPrep || h.hwPrepPairActiveDoor === dbStrikePrep) return true;
        if(h.hwPrepPairInactiveDoor === dbPrep || h.hwPrepPairInactiveDoor === dbStrikePrep) return true;
        if(h.hwPrepPairFrame === dbPrep || h.hwPrepPairFrame === dbStrikePrep) return true;

        return false;
    }

    if(h.hwPrepSingleActiveDoor === dbPrep || h.hwPrepSingleActiveDoor === dbStrikePrep) return true;
    if(h.hwPrepSingleFrame === dbPrep || h.hwPrepSingleFrame === dbStrikePrep) return true;
    return false;
}

export function hasDeadbolt(list: Product[] | Pick<HardwareItem, TPreps>[], isPair: boolean) {
    return list.some(h => isDeadboltItem(h, isPair));
}

export function convertPrepConfig(prepConfig: PrepDetail[]|undefined) {
    let config: Partial<PrepValues>|undefined = undefined;

    if(prepConfig) {
        config = {};
        for(const prep of prepConfig) {
            // @ts-ignore
            config[prep.name] = {
                enabled: prep.enabled,
                value: prep.value ? ImperialDistance.parse(prep.value).universalValue() : null
            }
        }
    }

    return config;
}

export function convertPrepConfig2(prepConfig: PrepConfigCustomOffset[]|undefined) {
    let config: Partial<PrepValues>|undefined = undefined;

    if(prepConfig) {
        config = {};
        for(const prep of prepConfig) {
            // @ts-ignore
            config[prep.name] = {
                enabled: prep.enabled,
                value: prep.value ? ImperialDistance.parse(prep.value).universalValue() : null
            }
        }
    }

    return config;
}

export function PrepManager(props: {
    title?: string;
    clearExisting?: boolean;
    value: HardwareGroupWithContent;
    noRegularCancel?: boolean;
    onCancel(): void;
    onDone(preps: PrepDetail[]): Promise<void>;
}) {

    const {heights, widths, inactiveWidths,
        hingeHeight, overrideHingeQty,
        hasDeadbolt, isPair
    } = usePrepManagerDetail({
        value: props.value,
        clearExisting: props.clearExisting,
    });

    console.log("usePrepManagerDetail", heights, widths, inactiveWidths, hingeHeight, overrideHingeQty, hasDeadbolt, isPair);

    const groupByType = groupByArr(props.value.openings, v => v.openingType)

    if(groupByType.length > 1) {
        return (
            <Dialog2 open onClose={props.onCancel}>
                <DialogTitle>{props.title || "Customize Hardware Preps"}</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        You have different opening types on the doors in this group. This will cause issues with the diagrams.
                        {groupByType.map(v =>
                            <div key={v.key}>{v.key + ": " + v.values.map(v => v.name).join(", ")}</div>
                        )}
                    </Alert>
                </DialogContent>
                <DialogContent>
                    <Button variant="contained" color="primary" onClick={props.onCancel}>Close</Button>
                </DialogContent>
            </Dialog2>
        );
    }

    if(heights.length > 1) {
        return (
            <Dialog2 open onClose={props.onCancel}>
                <DialogTitle>{props.title || "Customize Hardware Preps"}</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        You have different heights on the doors in this group. This will cause issues with the diagrams.
                        {props.value.openings.map(o => o.name + ": " + o.nominalHeight).join(", ")}
                    </Alert>
                </DialogContent>
                <DialogContent>
                    <Button variant="contained" color="primary" onClick={props.onCancel}>Close</Button>
                </DialogContent>
            </Dialog2>
        );
    }

    if(heights.filter(v => !!v).length === 0) {
        return (
            <Dialog2 open onClose={props.onCancel}>
                <DialogTitle>{props.title || "Customize Hardware Preps"}</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        You're missing nominal heights or doors in this group.
                        Found: {props.value.openings.map(o => o.name + ": " + (o.nominalHeight || "missing height")).join(", ")}
                    </Alert>
                </DialogContent>
                <DialogContent>
                    <Button variant="contained" color="primary" onClick={props.onCancel}>Close</Button>
                </DialogContent>
            </Dialog2>
        );
    }

    return (
        <InnerPrepManager
            ignoredBy={prepIgnoredBy(props.value.openings)}
            openingCount={props.value.openings.length}
            prepConfigJson={props.clearExisting ? undefined : props.value.prepConfigJson}
            title={props.title}
            onDone={props.onDone}
            onCancel={props.onCancel}
            noRegularCancel={props.noRegularCancel}
            height={heights[0] || ""}
            width={widths[0] || ""}
            inactiveWidth={inactiveWidths[0] || ""}
            hingeHeight={hingeHeight}
            overrideHingeQty={overrideHingeQty}
            hasDeadbolt={hasDeadbolt}
            isPair={isPair}
        />
    )
}

type PrepField = keyof PrepFromGroup;
const prepFields: PrepField[] = ["overCutDoor", "underCutDoor", "underCutFrame", "hardware"];

// prepIgnoredBy lists, for each prep field, the openings that take it from their drw elevations
// instead of the group's preps.
function prepIgnoredBy(openings: {name: string; prepFromGroup?: PrepFromGroup}[]): Record<PrepField, string[]> {
    const out: Record<PrepField, string[]> = {overCutDoor: [], underCutDoor: [], underCutFrame: [], hardware: []};

    for(const o of openings) {
        if(!o.prepFromGroup) continue;

        for(const field of prepFields) {
            if(!o.prepFromGroup[field]) out[field].push(o.name);
        }
    }

    return out;
}

// IgnoredNote names the openings that ignore a prep field, when only some of them do.
function IgnoredNote(props: {names: string[]}) {
    if(props.names.length === 0) return null;
    return <div style={{fontSize: "0.75rem", color: "#757575", maxWidth: 200}}>Ignored for: {props.names.join(", ")} (drw)</div>
}

export function InnerPrepManager(props: {
    ignoredBy?: Record<PrepField, string[]>;
    openingCount?: number;

    height: string;
    width: string;
    inactiveWidth: string;
    isPair: boolean;
    hingeHeight: number;
    overrideHingeQty?: number;
    hasDeadbolt: boolean;

    prepConfigJson?: PrepDetail[];
    title?: string;

    onDone(input: PrepDetail[]): Promise<void>;

    noRegularCancel?: boolean;
    onCancel(): void;
}) {
    const prepConfig = props.prepConfigJson;
    const [overCutDoorStr, setOverCutDoorStr] = useState<string>(prepConfig?.find(v => v.name === "overCutDoor")?.value || "");
    const [underCutDoorStr, setUnderCutDoorStr] = useState<string>(prepConfig?.find(v => v.name === "underCutDoor")?.value || "");
    const [underCutFrameStr, setUnderCutFrameStr] = useState<string>(prepConfig?.find(v => v.name === "underCutFrame")?.value || "");

    const manager = usePrepManager2({
        height: props.height,
        width: props.width,
        inactiveWidth: props.inactiveWidth,
        isPair: props.isPair,
        config: props.prepConfigJson,
        hingeHeight: props.hingeHeight,
        overrideHingeQty: props.overrideHingeQty,
        hasDeadbolt: props.hasDeadbolt,
        underCutDoor: underCutDoorStr,
        overCutDoor: overCutDoorStr,
        underCutFrame: underCutFrameStr,
    });

    const onDoneRef = useSyncedRef(props.onDone)
    const canvasRef = useRef<null|CanvasRef>(null);

    // a field that no opening takes from the group is hidden; its stored value is still saved as-is
    const ignoredBy = (field: PrepField) => props.ignoredBy?.[field] || [];
    const hidden = (field: PrepField) => !!props.openingCount && ignoredBy(field).length === props.openingCount;
    const partlyIgnored = (field: PrepField) => hidden(field) ? [] : ignoredBy(field);

    const save = useAsyncAction(async (input: PrepDetail[]) => {
        await onDoneRef.current(input)
    }, []);

    const submit = () => {
        const list = manager.options().map(o => ({
            // if changing this structure, see also core/product/main.go
            value: o.value.value ? ImperialDistance.showInches(o.value.value) : null,
            name: o.name,
            enabled: o.value.enabled
        }));

        if(overCutDoorStr !== "") {
            list.push({
                value: overCutDoorStr,
                name: "overCutDoor",
                enabled: true
            })
        }

        if(underCutDoorStr !== "") {
            list.push({
                value: underCutDoorStr,
                name: "underCutDoor",
                enabled: true
            })
        }

        if(underCutFrameStr !== "") {
            list.push({
                value: underCutFrameStr,
                name: "underCutFrame",
                enabled: true
            })
        }

        save.callback(list);
    };

    return (
        <Dialog2 open fullWidth maxWidth="lg" onClose={!props.noRegularCancel ? props.onCancel : undefined} onEnterPress={submit}>
            <DialogTitle>{props.title || "Customize Hardware Preps"}</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item xs>
                        {hidden("hardware") ?
                            <Alert severity="info">Hinge and lock locations come from the drw hardware elevation.</Alert> :
                            <>
                                {partlyIgnored("hardware").length > 0 && <Alert severity="info" style={{marginBottom: 8}}>
                                    Hinge and lock locations are ignored for: {partlyIgnored("hardware").join(", ")} (drw hardware elevation)
                                </Alert>}
                                <PrepCanvas canvasRef={canvasRef} manager={manager} />
                            </>
                        }
                        <div style={{marginTop: 16}}>
                            <Grid container spacing={1}>
                                {!hidden("overCutDoor") && <Grid item>
                                    <DimensionInput2 label="Overcut Door" inches value={overCutDoorStr} onChange={setOverCutDoorStr} />
                                    <IgnoredNote names={partlyIgnored("overCutDoor")} />
                                </Grid>}
                                {!hidden("underCutDoor") && <Grid item>
                                    <DimensionInput2 label="Undercut Door" inches value={underCutDoorStr} onChange={setUnderCutDoorStr} />
                                    <IgnoredNote names={partlyIgnored("underCutDoor")} />
                                </Grid>}
                                {!hidden("underCutFrame") && <Grid item>
                                    <DimensionInput2 label="Undercut Frame" inches value={underCutFrameStr} onChange={setUnderCutFrameStr} />
                                    <IgnoredNote names={partlyIgnored("underCutFrame")} />
                                </Grid>}
                            </Grid>
                        </div>
                        {!hidden("hardware") && <div style={{marginTop: 16}}>
                            <AutoCalculate
                                nominalHeight={props.height}
                                manager={manager}
                                doorOverCut={overCutDoorStr}
                                doorUnderCut={underCutDoorStr}
                                frameUnderCut={underCutFrameStr}
                            />
                        </div>}
                    </Grid>
                    {!hidden("hardware") && <Grid item>
                        <PrepControls manager={manager} />
                    </Grid>}
                </Grid>
            </DialogContent>
            <SmoothActionBar
                loading={save.loading}
                error={save.error}
                onCancel={props.noRegularCancel ? undefined : props.onCancel}
                onSubmit={submit}
            />
        </Dialog2>
    )
}

export function safeDimParse(value: string) {
    try {
        if(!value) return undefined;
        return genericDistanceParser(value).universalValue()
    } catch (e) {
        return undefined;
    }
}